import type { ReactNode } from "react";
import type { BlogPostViewModel } from "@/models/blog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPageList,
  PaginationPrevious,
  Tag,
  Typography,
} from "@/components/ui/atoms";
import { PostCard } from "../cards";
import { EmptyStatePanel } from "../controls";
import { ListingTemplate } from "../listing-template";

function BlogListingIntro(): ReactNode {
  return (
    <>
      <Tag variant="subtle" intent="primary" palette="brand">
        Read Our Blog
      </Tag>
      <Typography variant="h1" className="mt-3 sm:mt-4">
        Browse Our Resources
      </Typography>
      <Typography as="p" variant="body-md" color="muted" className="mt-1 max-w-2xl">
        We provide tips and resources from industry leaders.
      </Typography>
    </>
  );
}

export interface BlogListingSectionProps {
  featured: ReactNode;
  controls: ReactNode | null;
  /** Article list for the grid; when null and not loading, the content area renders nothing. */
  gridPosts: BlogPostViewModel[] | null;
  isLoading?: boolean;
  hasFiltersApplied: boolean;
  onClearFilters: () => void;
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
}

export function BlogListingSection({
  featured,
  controls,
  gridPosts,
  isLoading = false,
  hasFiltersApplied,
  onClearFilters,
  currentPage = 1,
  totalPages = 1,
  pageSize = 6,
  totalItems = 0,
  onPageChange,
}: BlogListingSectionProps): ReactNode {
  const showPagination = Boolean(onPageChange) && totalItems > pageSize;

  return (
    <ListingTemplate
      header={<BlogListingIntro />}
      featured={featured}
      controls={controls}
      content={
        isLoading ? (
          <ul
            className="grid list-none grid-cols-1 gap-4 p-0 sm:gap-6 md:grid-cols-2 xl:grid-cols-3"
            role="status"
            aria-label="Loading articles"
            aria-busy={true}
          >
            {Array.from({ length: pageSize }).map((_, index) => (
              <li key={index}>
                <PostCard isLoading />
              </li>
            ))}
          </ul>
        ) : gridPosts === null ? null : gridPosts.length > 0 ? (
          <>
            <ul className="grid list-none grid-cols-1 gap-4 p-0 sm:gap-6 md:grid-cols-2 xl:grid-cols-3">
              {gridPosts.map((post) => (
                <li key={post.documentId}>
                  <PostCard post={post} />
                </li>
              ))}
            </ul>
            {showPagination ? (
              <Pagination className="mt-10 w-full justify-center">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => onPageChange?.(currentPage - 1)}
                      disabled={currentPage <= 1}
                    />
                  </PaginationItem>
                  <PaginationPageList
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => onPageChange?.(page)}
                  />
                  <PaginationItem>
                    <PaginationNext
                      onClick={() => onPageChange?.(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            ) : null}
          </>
        ) : (
          <EmptyStatePanel hasFiltersApplied={hasFiltersApplied} onClearFilters={onClearFilters} />
        )
      }
    />
  );
}
